import { Component, computed, inject, input, ViewChild } from '@angular/core';
import { findStateForMetric, getColours } from '@dxp/core/helpers';
import { IMetric, MetricPaletteType } from '@dxp/core/models';
import { HeartbeatService } from '@dxp/core/services';
import { ChartConfiguration } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

/**
 * Component to display nested ring charts.
 * Each ring represents a percentage value with a colour code indicating the status.
 *
 * @selector rings
 * @standalone true
 * @component RingsComponent
 * @imports CommonModule, BaseChartDirective
 *
 * @Input metrics - Array of objects conforming to IMetric interface, each representing a ring in the chart.
 * @Input label - Text to display.
 * @Input overall - The summary value for the data.
 *
 * Usage:
 * <telxl-rings [metric]="data" [label]="label" [overall]="overall"></rings>
 *
 * Note:
 * A data item contains the percent value of the ring and state change values.
 * The state change value is an array of numbers to set the RAG status.
 *
 * e.g. { percent: 80, state: [{25, 'success'}, {75, 'danger'}]}
 * In this scenario a percent value over 25 will render success state and over 75 will render danger state.
 *
 * e.g. { percent: 30, state: [{50, 'warning'}]}
 * In this scenario a percent value over 50 will render warning state. No red state is configured.
 *
 */

const DEFAULT_CUTOUT = 97;

@Component({
    selector: 'telxl-rings',
    standalone: true,
    imports: [BaseChartDirective],
    templateUrl: './rings.component.html',
    styleUrl: './rings.component.scss',
})
export class TelXLRingsComponent {
    readonly #heartbeatService = inject(HeartbeatService);

    defaultState = input<MetricPaletteType>('success');
    emptyState = input<MetricPaletteType>('dark');
    label = input.required<string>();
    percentage = input<boolean>(false);
    metrics = input.required<IMetric[]>();
    height = input.required<string>();
    width = input.required<string>();
    overall = input.required<string>();

    @ViewChild(BaseChartDirective, { static: false })
    chart?: BaseChartDirective;

    readonly cutout = computed<string>(
        () => `${DEFAULT_CUTOUT - this.metrics().length * 10}%`,
    );

    readonly ringsChartOptions = computed<
        ChartConfiguration<'doughnut'>['options']
    >(() => {
        return {
            cutout: this.cutout,
            responsive: true,
            animation: false,
            plugins: {
                tooltip: { enabled: false },
                legend: { display: false },
            },
        };
    });

    ringsChartType: ChartConfiguration<'doughnut'>['type'] = 'doughnut';

    readonly ringsChartData = computed<ChartConfiguration<'doughnut'>['data']>(
        () => {
            this.#heartbeatService.lastHeartbeat();

            return {
                datasets: this.metrics().flatMap((value, index) => {
                    const state = findStateForMetric(
                        value,
                        this.defaultState(),
                        this.emptyState(),
                    );

                    const colours = getColours();
                    const colour = colours[state as keyof typeof colours];

                    const ring = {
                        data: [value.percent, 100 - value.percent],
                        backgroundColor: [colour, colours['background']],
                        hoverBackgroundColor: [colour, colours['background']],
                        borderWidth: 0,
                        borderRadius: 20,
                    };

                    const blank = {
                        data: [100, 0],
                        backgroundColor: ['#FFFFFFFF', '#FFFFFFFF'],
                        hoverBackgroundColor: ['#FFFFFFFF', '#FFFFFFFF'],
                        borderWidth: 0,
                        borderRadius: 0,
                        weight: 0.5,
                    };

                    if (index < this.metrics().length - 1) return [ring, blank];
                    else return [ring];
                }),
            };
        },
    );
}
